<script setup>
  import { storeToRefs } from "pinia";
  import { Settings2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { useCampaignsPivotStore } from "@stores/useCampaignsPivotStore";
  import { PIVOT_METRIC_GROUPS, DEFAULT_METRICS } from "../pivotMetrics";

  const store = useCampaignsPivotStore();
  const { selectedMetrics } = storeToRefs(store);

  function isSelected(key) {
    return selectedMetrics.value.includes(key);
  }

  function toggleMetric(key) {
    if (isSelected(key)) {
      selectedMetrics.value = selectedMetrics.value.filter((k) => k !== key);
    } else {
      selectedMetrics.value = [...selectedMetrics.value, key];
    }
  }

  // Column dimensions are usually read with a single metric: "only" gets there in one click.
  const onlyMetric = (key) => (selectedMetrics.value = [key]);
  const clearMetrics = () => (selectedMetrics.value = []);
  const resetMetrics = () => (selectedMetrics.value = [...DEFAULT_METRICS]);
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button
        variant="outline"
        size="sm"
        class="gap-2 bg-white shrink-0">
        <Settings2 class="size-4" />
        Columns
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      class="w-60">
      <div class="flex items-center justify-between px-2 py-1.5 text-xs">
        <button
          class="text-muted-foreground hover:text-foreground"
          @click.stop.prevent="clearMetrics">
          Clear all
        </button>
        <button
          class="text-muted-foreground hover:text-foreground"
          @click.stop.prevent="resetMetrics">
          Reset to defaults
        </button>
      </div>
      <template
        v-for="group in PIVOT_METRIC_GROUPS"
        :key="group.label">
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{{ group.label }}</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          v-for="m in group.metrics"
          :key="m.key"
          :model-value="isSelected(m.key)"
          class="group/metric"
          @select.prevent="toggleMetric(m.key)">
          <span class="flex-1">{{ m.label }}</span>
          <button
            class="ml-2 text-3xs uppercase tracking-wide text-muted-foreground opacity-0 group-hover/metric:opacity-100 hover:text-foreground"
            @pointerdown.stop
            @pointerup.stop
            @click.stop.prevent="onlyMetric(m.key)">
            only
          </button>
        </DropdownMenuCheckboxItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
